import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * „BETA" beside the wordmark — and a door, not a disclaimer.
 *
 * A bare chip is decoration: every product wears one, nobody reads one, and it
 * says nothing a grown-up can act on. What makes it honest is what it opens —
 * two sentences on *what* is unfinished and one concrete instruction for the
 * moment they meet it. So the badge is a button, and the explanation is the
 * whole point of adding it.
 *
 * A dialog rather than a tooltip, because hover does not exist on the tablets
 * this is read on — the same reason `FigureLightbox` opens on a tap.
 *
 * **Never in the learner's shell**, which is why this lives beside the wordmark
 * and the wordmark is not there. A child cannot act on "beta", and telling one
 * that the app is unfinished is telling a stuck eight-year-old that the
 * question might be wrong — the one excuse this app is careful never to hand
 * them. Their version of this signal already exists, is scoped to the thing
 * that is actually broken, and reaches the same queue: „Kažkas negerai su šia
 * užduotimi", on the question itself.
 *
 * Neither `destructive` (which reads as broken) nor `primary` (which competes
 * with the mark it stands next to), and deliberately not `--brand-accent` —
 * that token is reserved for what a learner has earned. A quiet outline is the
 * right register: a note, not a warning.
 */
export function BetaBadge({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Badge
        asChild
        variant="outline"
        className={className}
        // `asChild` so the chip *is* the button: a button wrapped around a
        // badge is two focus rings and two hit areas for one control.
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Beta versija — ką tai reiškia"
          className="cursor-pointer px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          Beta
        </button>
      </Badge>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scio yra beta versija</DialogTitle>
            <DialogDescription>
              Programa veikia ir naudojama kasdien, bet dar nėra baigta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              Dalis dalykų dar tvarkoma, todėl kur nors pasitaikys nesutvarkytas kampas:
              nenusipiešęs brėžinys, nerangiai suformuluotas klausimas ar ekranas, kuris
              atrodo nelygiai. Tai žinoma ir taisoma.
            </p>
            <p>
              Vaiko atsakymai, pažanga ir laiškai saugomi kaip įprastai — beta reiškia
              neužbaigtus dalykus, ne prarandamą darbą.
            </p>
            <p className="text-foreground">
              <strong className="font-medium">Radus klaidą:</strong> pačioje užduotyje
              spauskite „…" → „Kažkas negerai su šia užduotimi". Tai patenka į pažymėto
              turinio sąrašą ir yra peržiūrima. Tą patį mygtuką turi ir vaikas.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
